const extractBeneficiaryName = title => {
  if (!title || typeof title !== 'string') return null
  const match = title.match(/\d{26}\s*([A-Za-zĄĆĘŁŃÓŚŹŻąćęłńóśźż\s\-]+)\s*$/)
  return match ? match[1].trim() : null
}

/** Usuwa wszystkie podwójne cudzysłowy (ASCII + typograficzne) – psują dopasowanie do ucznia. */
const removeQuotesFromTitle = s =>
  s.replace(/[\u0022\u201c\u201d\u201e\u201f]/g, '')

/** Czyści opis operacji z HTML zanim trafi do CSV (znaki końca linii, zero-width, cudzysłowy). */
const sanitizeTitleFromBankHtml = raw => {
  if (raw == null || typeof raw !== 'string') return ''
  let s = raw.replace(/[\u200b-\u200d\ufeff]/g, '')
  s = s.replace(/\r\n?|\n|\u2028|\u2029/g, ' ')
  s = s.replace(/\s+/g, ' ').trim()
  s = removeQuotesFromTitle(s)
  s = s.replace(/\s+/g, ' ').trim()
  return s
}

const parseTotalFromHtml = doc => {
  const cells = Array.from(doc.querySelectorAll('td, th'))
  const lacznieCell = cells.find(c => c.textContent?.trim() === 'Łącznie')
  if (!lacznieCell) return null
  const row = lacznieCell.closest('tr')
  if (!row) return null
  const rowCells = Array.from(row.children).filter(c => c.tagName === 'TD' || c.tagName === 'TH')
  if (rowCells.length < 3) return null
  const valueStr = (rowCells[2].textContent || '').replace(/\s/g, '').replace(',', '.').replace(/PLN/gi, '').trim()
  return parseFloat(valueStr) || null
}

export const parseHtmlToOperations = html => {
  const operations = []
  let total = null
  if (!html || typeof html !== 'string') return { operations, total }

  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  total = parseTotalFromHtml(doc)

  const headerCell = Array.from(doc.querySelectorAll('td, th')).find(
    cell => cell.textContent?.trim() === 'Data operacji'
  )
  const operationsTable = headerCell?.closest('table')
  if (!operationsTable) return { operations, total }

  const rows = operationsTable.querySelectorAll('tbody tr, tr')
  let colIndices = null

  for (const row of rows) {
    const cells = Array.from(row.children).filter(c => c.tagName === 'TD' || c.tagName === 'TH')
    if (cells.length < 5) continue

    const cellTexts = cells.map(c => c.textContent?.trim() ?? '')
    if (cellTexts.includes('Data księgowania')) {
      colIndices = {
        date: cellTexts.indexOf('Data księgowania'),
        title: cellTexts.indexOf('Opis operacji'),
        amount: cellTexts.indexOf('Kwota')
      }
      continue
    }
    if (!colIndices) continue

    const firstCell = cells[0]
    if (firstCell.colSpan > 1) continue

    const date = cellTexts[colIndices.date] || ''
    const rawTitle = cellTexts[colIndices.title] || ''
    const titleForRow = sanitizeTitleFromBankHtml(rawTitle)
    const beneficiary = extractBeneficiaryName(titleForRow)
    const amountStr = (cellTexts[colIndices.amount] || '').replace(/\s/g, '').replace(',', '.')
    const amount = parseFloat(amountStr) || 0

    if (!date || !amount) continue

    if (beneficiary) {
      operations.push({ amount, date, title: beneficiary, extracted: true })
    } else {
      operations.push({ amount, date, title: titleForRow, extracted: false })
    }
  }

  return { operations, total }
}

const csvFieldNeedsQuotes = str => /[",\n\r\t]/.test(str)

const escapeCsvField = val => {
  const str = String(val ?? '')
  if (csvFieldNeedsQuotes(str)) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

export const operationsToCsv = (operations, total = null) => {
  const lines = []
  if (total != null && !isNaN(total)) {
    lines.push(`#total,${String(total).replace(',', '.')}`)
  }
  lines.push('date,amount,title,extracted')
  operations.forEach(op => {
    lines.push([escapeCsvField(op.date), escapeCsvField(op.amount), escapeCsvField(op.title), escapeCsvField(op.extracted ? '1' : '0')].join(','))
  })
  return lines.join('\n')
}

const parseCsvLine = line => {
  const result = []
  let current = ''
  let inQuotes = false
  for (let j = 0; j < line.length; j++) {
    const c = line[j]
    if (c === '"') {
      if (inQuotes && line[j + 1] === '"') {
        current += '"'
        j++
      } else {
        inQuotes = !inQuotes
      }
    } else if (c === ',' && !inQuotes) {
      result.push(current.trim())
      current = ''
    } else {
      current += c
    }
  }
  result.push(inQuotes ? current : current.trim())
  return result
}

export const parseCsvToOperations = csvText => {
  const operations = []
  let total = null
  if (!csvText || typeof csvText !== 'string') return { operations, total }

  const lines = csvText.split(/\r?\n/).filter(Boolean)
  if (lines.length < 2) return { operations, total }

  let dataStartIdx = 0
  if (lines[0].trim().toLowerCase().startsWith('#total')) {
    const totalFields = parseCsvLine(lines[0])
    const totalStr = (totalFields[1] || totalFields[0] || '').replace(/\s/g, '').replace(',', '.')
    total = parseFloat(totalStr) || null
    dataStartIdx = 1
  }

  const headerFields = parseCsvLine(lines[dataStartIdx]).map(f => f.toLowerCase())
  const dateIdx = headerFields.indexOf('date') >= 0 ? headerFields.indexOf('date') : 0
  const amountIdx = headerFields.indexOf('amount') >= 0 ? headerFields.indexOf('amount') : 1
  const titleIdx = headerFields.indexOf('title') >= 0 ? headerFields.indexOf('title') : 2
  const hasExtractedColumn = headerFields.includes('extracted')
  const extractedIdx = headerFields.indexOf('extracted')

  for (let i = dataStartIdx + 1; i < lines.length; i++) {
    const fields = parseCsvLine(lines[i])
    const date = (fields[dateIdx] || '').trim()
    const amountStr = (fields[amountIdx] || '').replace(/\s/g, '').replace(',', '.')
    const amount = parseFloat(amountStr) || 0
    const title = (fields[titleIdx] || '').trim()

    if (!date || !amount || !title) continue

    const extracted = hasExtractedColumn && (fields[extractedIdx] || '').trim() === '1'

    operations.push({ amount, date, title, extracted })
  }

  return { operations, total }
}
